import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { FiEye, FiEyeOff, FiUser, FiHome, FiMail, FiLock, FiTag, FiInfo, FiX, FiMapPin, FiGlobe, FiChevronDown } from 'react-icons/fi';
import { ARCHERY_CLUBS } from '../constants/ArcheryClubs';

// --- ÚJ: ORSZÁG LISTA A GLOBÁLIS NYITÁSHOZ ---
const COUNTRIES = [
  "Magyarország", "Ausztria", "Szlovákia", "Románia", "Szerbia", "Horvátország", "Szlovénia",
  "Németország", "Olaszország", "Franciaország", "Egyesült Királyság", "Spanyolország",
  "USA", "Kanada", "Ausztrália", "Lengyelország", "Csehország", "Ukrajna", "Belgium", "Hollandia"
].sort();

const RegisterForm = () => {
  const [regMode, setRegMode] = useState('user'); // 'user' = Versenyző, 'club' = Egyesület
  const [onlyAthlete, setOnlyAthlete] = useState(false);

  // MODAL ÁLLAPOT
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // VERSENYZŐI ADATOK
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [ageChecked, setAgeChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [privacyOpened, setPrivacyOpened] = useState(false);
  const [ageError, setAgeError] = useState('');

  // KÖZÖS ÉS EGYESÜLETI ADATOK
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [clubName, setClubName] = useState(''); // Csak egyesületnek
  const [clubLocation, setClubLocation] = useState(''); // ÚJ
  const [clubCountry, setClubCountry] = useState(''); // ÚJ (Üresen indul)
  const [countrySuggestions, setCountrySuggestions] = useState([]); // ÚJ
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false); // ÚJ
  const [clubSuggestions, setClubSuggestions] = useState([]); // ÚJ
  const [showClubDropdown, setShowClubDropdown] = useState(false); // ÚJ
  const [isManualClub, setIsManualClub] = useState(false); // ÚJ
  const [registeredClubNames, setRegisteredClubNames] = useState(new Set()); // ÚJ

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    // Kontextus ellenőrzése (nevezés közben érkezett-e?)
    const athleteOnly = sessionStorage.getItem('onlyAthlete');
    if (athleteOnly === 'true') {
      setOnlyAthlete(true);
      setRegMode('user');
    } else {
      // BIZTONSÁGI TISZTÍTÁS: Ha nem nevezésből jövünk, ne maradjon bent stale redirect
      const staleRedirect = sessionStorage.getItem('redirectAfterLogin');
      if (staleRedirect && staleRedirect.includes('autoOpen=true')) {
          sessionStorage.removeItem('redirectAfterLogin');
      }
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- ÚJ: REGISZTRÁLT KLUBOK BETÖLTÉSE ---
  useEffect(() => {
    const fetchRegisteredClubs = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "Clubs"));
        const names = new Set();
        querySnapshot.forEach((doc) => {
          if (doc.data().clubName) {
            names.add(doc.data().clubName.trim().toUpperCase());
          }
        });
        setRegisteredClubNames(names);
      } catch (e) {
        console.error("Hiba a regisztrált klubok betöltésekor:", e);
      }
    };
    fetchRegisteredClubs();
  }, []);

  // Életkor ellenőrzése
  useEffect(() => {
    if (birthDate && regMode === 'user') {
      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }

      if (age < 16) {
        setAgeError(`Sajnos ${age} évesen még nem regisztrálhatsz önállóan (min. 16 év).`);
        setAgeChecked(false);
      } else {
        setAgeError('');
      }
    } else {
      setAgeError('');
    }
  }, [birthDate, regMode]);

  // --- ÚJ: ORSZÁG AUTOCOMPLETE LOGIKA ---
  const handleCountryChange = (val) => {
    setClubCountry(val);
    if (val.length > 0) {
      const filtered = COUNTRIES.filter(c => c.toLowerCase().includes(val.toLowerCase()));
      setCountrySuggestions(filtered);
      setShowCountrySuggestions(filtered.length > 0);
    } else {
      setShowCountrySuggestions(false);
    }
  };

  // --- ÚJ: KLUB NÉV FORMÁZÓ SEGÉDFÜGGVÉNY ---
  const formatClubName = (name) => {
    if (!name || name === "Egyéb") return name;
    const specialCases = {
        "3d": "3D", "kft.": "Kft.", "se": "SE", "sk": "SK", "dse": "DSE",
        "kse": "KSE", "mavir": "MAVIR", "ifaa": "IFAA", "hdh-iaa": "HDH-IAA"
    };

    return name.split(' ').map(word => {
      const cleanWord = word.replace(/[^a-zA-ZáéíóöőúüűÁÉÍÓÖŐÚÜŰ0-9]/g, '').toLowerCase();
      if (specialCases[cleanWord]) return specialCases[cleanWord];
      if (word.startsWith('"')) {
          return '"' + (word.charAt(1)?.toUpperCase() || "") + word.slice(2).toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
  };

  // --- ÚJ: EGYESÜLET AUTOCOMPLETE LOGIKA ---
  const handleClubNameChange = (val) => {
    setClubName(val);
    if (!isManualClub && val.length > 0) {
      const filtered = ARCHERY_CLUBS
        .filter(c => !registeredClubNames.has(c.toUpperCase())) // KISZŰRJÜK A FOGLALTAKAT
        .filter(c => c.toLowerCase().includes(val.toLowerCase()))
        .map(c => formatClubName(c)); // FORMÁZZUK A MEGJELENÍTÉSHEZ
      setClubSuggestions(filtered);
      setShowClubDropdown(true);
    } else {
      setShowClubDropdown(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    // Validáció versenyzőnek
    if (regMode === 'user') {
        if (!lastName || !firstName || !birthDate) {
            setError('Kérjük tölts ki minden mezőt!');
            return;
        }
        if (!privacyChecked || !privacyOpened) {
            setError('Kérjük olvasd el és fogadd el az adatvédelmi tájékoztatót!');
            return;
        }
        if (!ageChecked || ageError) {
            setError('A regisztrációhoz el kell fogadnod a nyilatkozatot és legalább 16 évesnek kell lenned!');
            return;
        }
    } else {
        // Validáció egyesületnek
        if (!clubName) {
            setError('Kérjük add meg az egyesület nevét!');
            return;
        }
    }

    setLoading(true);
    try {
      // 1. Regisztráció az Authentication-be
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 2. Kollekció meghatározása
      const collectionName = regMode === 'user' ? 'users' : 'Clubs';
      
      // SAJÁT KÓD GENERÁLÁSA (Android app stílus)
      const myShortCode = user.uid.length >= 5 ? user.uid.slice(-5).toUpperCase() : user.uid.toUpperCase();

      // Adatok összeállítása
      const userData = {
        uid: user.uid,
        email: user.email,
        createdAt: serverTimestamp(),
        role: regMode
      };

      if (regMode === 'user') {
        userData.lastName = lastName;
        userData.firstName = firstName;
        userData.displayName = `${lastName} ${firstName}`.trim();
        userData.birthDate = new Date(birthDate).getTime();
        userData.referralShortCode = myShortCode;
        userData.referredCount = 0;
        userData.trophies = [];
      } else {
        userData.clubName = clubName;
        userData.location = clubLocation;
        userData.country = clubCountry;
        if (isManualClub) {
          userData.isNewClubRequest = true;
        }
      }

      // 3. Mentés Firestore-ba
      await setDoc(doc(db, collectionName, user.uid), userData);

      // Takarítás
      sessionStorage.removeItem('onlyAthlete');

      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        navigate(regMode === 'club' ? '/egyesulet-dashboard' : '/profil');
      }
    } catch (err) {
      console.error('❌ Hiba:', err.message);
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('Ez az email cím már használatban van');
          break;
        case 'auth/invalid-email':
          setError('Érvénytelen email cím');
          break;
        case 'auth/weak-password':
          setError('A jelszónak legalább 6 karakter hosszúnak kell lennie');
          break;
        default:
          setError('Hiba történt a regisztráció során');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fül stílusok (ugyanaz mint a Loginnál)
  const tabStyle = (mode) => ({
    flex: 1,
    padding: '15px',
    cursor: 'pointer',
    border: 'none',
    fontSize: '1rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: '0.3s',
    backgroundColor: regMode === mode ? '#fff' : '#f2f2f2',
    color: regMode === mode ? '#b30000' : '#666',
    borderBottom: regMode === mode ? 'none' : '2px solid #b30000',
    borderRadius: mode === 'user' ? '12px 0 0 0' : '0 12px 0 0',
  });

  const inputGroupStyle = {
    marginBottom: '1rem',
    position: 'relative'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.8rem 1rem 0.8rem 2.8rem',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '1rem',
    boxSizing: 'border-box',
    outline: 'none',
    transition: '0.3s'
  };

  const iconStyle = {
    position: 'absolute',
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#b30000',
    fontSize: '1.2rem'
  };

  const infoBoxStyle = {
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '10px',
    padding: isMobile ? '12px' : '15px',
    marginBottom: '1.5rem',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  };

  const modalOverlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center',
    alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(3px)'
  };

  const modalContentStyle = {
    backgroundColor: '#fff', width: '90%', maxWidth: '700px', borderRadius: '15px',
    boxShadow: '0 15px 35px rgba(0,0,0,0.3)', overflow: 'hidden', textAlign: 'left',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column'
  };

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '10px',
    cursor: 'pointer',
    textAlign: 'left'
  };

  const autocompleteContainerStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
    zIndex: 100,
    maxHeight: '200px',
    overflowY: 'auto'
  };

  const autocompleteItemStyle = {
    padding: '10px 15px',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    fontSize: '0.9rem',
    color: '#333',
    textAlign: 'left'
  };

  const isClubNameTaken = !isManualClub && registeredClubNames.has(clubName.trim().toUpperCase());

  const isRegisterDisabled = loading || (regMode === 'user' && (
    !lastName || !firstName || !birthDate || !privacyChecked || !ageChecked || !privacyOpened || !!ageError
  )) || (regMode === 'club' && (!clubName || isClubNameTaken || !email || !password || !clubLocation || !clubCountry || !privacyChecked || !ageChecked || !privacyOpened));

  return (
    <main style={{
      padding: isMobile ? '1rem' : '0 2rem 2rem 2rem',
      fontFamily: 'Arial, sans-serif',
      maxWidth: isMobile ? '500px' : '700px',
      margin: isMobile ? '10px auto' : '5px auto'

    }}>

      <div style={infoBoxStyle}>
        <FiInfo size={isMobile ? 20 : 24} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div style={{ color: '#92400e', fontSize: isMobile ? '0.85rem' : '0.95rem', lineHeight: '1.5' }}>
          <strong>Figyelem!</strong> Az ArcheryNexus külön kezeli a versenyzői és az egyesületi fiókokat.
          <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
            <li><strong>Versenyzőként:</strong> Használd a saját privát e-mail címedet.</li>
            <li><strong>Egyesületként:</strong> Ha versenyeket szeretnél rendezni, <strong>kötelező a hivatalos egyesületi e-mail címet</strong> használnod! (Kérjük, ne regisztrálj egyesületet a privát címeddel!)</li>
          </ul>
        </div>
      </div>

      <div style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', overflow: 'hidden' }}>

        {(!onlyAthlete && !isMobile) ? (
          <div style={{ display: 'flex', backgroundColor: '#b30000' }}>
            <button type="button" onClick={() => { setRegMode('user'); setError(''); }} style={tabStyle('user')}>
              <FiUser /> Versenyző
            </button>
            <button type="button" onClick={() => { setRegMode('club'); setError(''); }} style={tabStyle('club')}>
              <FiHome /> Egyesület
            </button>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#b30000', color: '#fff', padding: '15px', textAlign: 'center',
            fontWeight: 'bold', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
          }}>
            <FiUser /> Versenyzői Regisztráció
          </div>
        )}

        <div style={{ padding: isMobile ? '1.5rem' : '2rem' }}>
          <h2 style={{ textAlign: 'center', color: '#b30000', marginBottom: '2rem', fontSize: isMobile ? '1.4rem' : '1.8rem' }}>
            {regMode === 'user' ? 'Versenyző Regisztráció' : 'Egyesületi Regisztráció'}
          </h2>

          <form onSubmit={handleRegister}>
            {regMode === 'user' ? (
              // VERSENYZŐI MEZŐK (ANDROID STÍLUS)
              <>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ ...inputGroupStyle, flex: 1 }}>
                    <FiTag style={iconStyle} />
                    <input
                      type="text"
                      placeholder="Vezetéknév"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ ...inputGroupStyle, flex: 1 }}>
                    <FiTag style={iconStyle} />
                    <input
                      type="text"
                      placeholder="Keresztnév"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={inputGroupStyle}>
                  <FiMail style={iconStyle} />
                  <input
                    type="email"
                    placeholder="Email címed"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={inputStyle}
                  />
                </div>

                <div style={inputGroupStyle}>
                  <FiLock style={iconStyle} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Jelszó"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{ ...inputStyle, paddingRight: '3rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>

                <div style={inputGroupStyle}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#b30000', marginBottom: '5px', display: 'block' }}>SZÜLETÉSI DÁTUM</label>
                  <input
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    required
                    style={{ ...inputStyle, paddingLeft: '1rem' }}
                  />
                </div>

                {ageError && <div style={{ color: '#dc3545', fontSize: '0.8rem', marginBottom: '15px', fontWeight: 'bold' }}>{ageError}</div>}

                <div style={{ marginBottom: '20px' }}>
                    <button
                        type="button"
                        onClick={() => {
                            setShowPrivacyModal(true);
                        }}
                        style={{
                            width: '100%', backgroundColor: '#f2f2f2', border: 'none', padding: '10px',
                            borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '15px'
                        }}
                    >
                        Adatvédelmi tájékoztató megtekintése
                    </button>

                    <label style={checkboxContainerStyle}>
                        <input type="checkbox" checked={ageChecked} onChange={(e) => setAgeChecked(e.target.checked)} disabled={!!ageError} />
                        <span style={{ fontSize: '0.8rem', color: '#666' }}>Kijelentem, hogy adataim a valóságnak megfelelnek és elmúltam 16 éves.</span>
                    </label>

                    <label style={checkboxContainerStyle}>
                        <input type="checkbox" checked={privacyChecked} onChange={(e) => setPrivacyChecked(e.target.checked)} />
                        <span style={{ fontSize: '0.8rem', color: '#666' }}>Elolvastam és elfogadom az Adatvédelmi tájékoztatót.</span>
                    </label>
                </div>
              </>
            ) : (
              // EGYESÜLETI MEZŐK (PC-N BŐVÍTVE)
              <>
                <div style={{ marginBottom: '1rem' }}>
                  {isManualClub && (
                    <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#b30000', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <FiInfo size={14} /> Új egyesület rögzítése
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <FiHome style={{ ...iconStyle, zIndex: 10, transition: 'none' }} />
                      <input
                        type="text"
                        placeholder={isManualClub ? "Írd be az új egyesület pontos nevét..." : "Egyesület neve"}
                        value={clubName}
                        onChange={(e) => handleClubNameChange(e.target.value)}
                        required
                        style={{ ...inputStyle, paddingRight: isManualClub ? '1rem' : '2.5rem', paddingLeft: '2.8rem', transition: 'none' }}
                      />
                      {!isManualClub && (
                        <FiChevronDown
                          style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#b30000', cursor: 'pointer', fontSize: '1.2rem', zIndex: 10 }}
                          onClick={() => {
                            if (!showClubDropdown) {
                              const allAvailable = ARCHERY_CLUBS
                                .filter(c => !registeredClubNames.has(c.toUpperCase()))
                                .map(c => formatClubName(c));
                              setClubSuggestions(allAvailable);
                              setShowClubDropdown(true);
                            } else {
                              setShowClubDropdown(false);
                            }
                          }}
                        />
                      )}
                      {showClubDropdown && !isManualClub && (
                        <div style={{ ...autocompleteContainerStyle, maxHeight: '250px', zIndex: 100 }}>
                          {clubSuggestions.length > 0 ? (
                            clubSuggestions.map((club, idx) => (
                              <div
                                key={idx}
                                style={autocompleteItemStyle}
                                onClick={() => {
                                  setClubName(club);
                                  setShowClubDropdown(false);
                                }}
                              >
                                {club}
                              </div>
                            ))
                          ) : (
                            <div style={{ ...autocompleteItemStyle, color: '#999', fontStyle: 'italic' }}>Nincs találat</div>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsManualClub(!isManualClub);
                        setShowClubDropdown(false);
                      }}
                      style={{
                        padding: '12px 15px',
                        backgroundColor: isManualClub ? '#6c757d' : '#FFDD00',
                        color: isManualClub ? '#fff' : '#000',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        height: '48px', // Fix magasság az inputhoz igazítva
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {isManualClub ? 'Vissza a listához' : 'Nincs a listában'}
                    </button>
                  </div>
                  {isClubNameTaken && (
                    <div style={{ color: '#dc3545', fontSize: '0.75rem', fontWeight: 'bold', marginTop: '5px' }}>
                      ❌ Ezzel a névvel már regisztráltak egy egyesületet!
                    </div>
                  )}
                </div>

                <div style={inputGroupStyle}>
                  <FiMail style={iconStyle} />
                  <input
                    type="email"
                    placeholder="Egyesületi email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={inputStyle}
                  />
                </div>

                <div style={inputGroupStyle}>
                  <FiLock style={iconStyle} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Jelszó"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{ ...inputStyle, paddingRight: '3rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>

                {/* SZÉKHELY MEZŐ - FIXÁLT, ANIMÁCIÓ MENTES IGAZÍTÁSSAL */}
                <div style={inputGroupStyle}>
                  <FiMapPin style={{ ...iconStyle, zIndex: 10, transition: 'none' }} />
                  <input
                    type="text"
                    placeholder="Székhely / Település"
                    value={clubLocation}
                    onChange={(e) => setClubLocation(e.target.value)}
                    required
                    style={{ ...inputStyle, paddingLeft: '2.8rem', transition: 'none' }}
                  />
                </div>

                {/* ORSZÁG MEZŐ - FIXÁLT, ANIMÁCIÓ MENTES IGAZÍTÁSSAL */}
                <div style={inputGroupStyle}>
                  <FiGlobe style={{ ...iconStyle, zIndex: 10, transition: 'none' }} />
                  <input
                    type="text"
                    placeholder="Kezdj el gépelni az ország kereséséhez..."
                    value={clubCountry}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    required
                    style={{ ...inputStyle, paddingLeft: '2.8rem', transition: 'none' }}
                  />
                  {showCountrySuggestions && (
                    <div style={autocompleteContainerStyle}>
                      {countrySuggestions.map((country, idx) => (
                        <div
                          key={idx}
                          style={autocompleteItemStyle}
                          onClick={() => {
                            setClubCountry(country);
                            setShowCountrySuggestions(false);
                          }}
                        >
                          {country}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* JOGI NYILATKOZATOK EGYESÜLETNEK IS */}
                <div style={{ marginBottom: '20px', marginTop: '10px' }}>
                    <button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        style={{
                            width: '100%', backgroundColor: '#f2f2f2', border: 'none', padding: '10px',
                            borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '15px'
                        }}
                    >
                        Adatvédelmi tájékoztató megtekintése
                    </button>

                    <label style={checkboxContainerStyle}>
                        <input type="checkbox" checked={ageChecked} onChange={(e) => setAgeChecked(e.target.checked)} />
                        <span style={{ fontSize: '0.8rem', color: '#666' }}>Kijelentem, hogy az adatok a valóságnak megfelelnek.</span>
                    </label>

                    <label style={checkboxContainerStyle}>
                        <input type="checkbox" checked={privacyChecked} onChange={(e) => setPrivacyChecked(e.target.checked)} />
                        <span style={{ fontSize: '0.8rem', color: '#666' }}>Elolvastam és elfogadom az Adatvédelmi tájékoztatót.</span>
                    </label>
                </div>
              </>
            )}

            {error && (
              <div style={{ color: '#dc3545', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', padding: '10px', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem', textAlign: 'center' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isRegisterDisabled}
              style={{
                width: '100%',
                backgroundColor: isRegisterDisabled ? '#ccc' : '#b30000',
                color: 'white',
                padding: '1rem',
                borderRadius: '8px',
                border: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: isRegisterDisabled ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: '0.3s',
                marginTop: '1rem'
              }}
            >
              {loading ? 'Regisztráció...' : (regMode === 'user' ? 'Versenyzői fiók létrehozása' : 'Egyesületi fiók létrehozása')}
            </button>
          </form>

          <div style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.9rem' }}>
            <span style={{ color: '#666' }}>Már van fiókod? </span>
            <Link to="/" style={{ color: '#b30000', fontWeight: 'bold', textDecoration: 'none' }}>
              Jelentkezz be
            </Link>
          </div>
        </div>
      </div>

      {/* --- ADATVÉDELMI MODAL --- */}
      {showPrivacyModal && (
          <div style={modalOverlayStyle} onClick={() => { setShowPrivacyModal(false); setPrivacyOpened(true); }}>
              <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                  <div style={{ backgroundColor: '#b30000', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0 }}>Adatvédelmi Tájékoztató</h3>
                      <FiX style={{ cursor: 'pointer' }} size={24} onClick={() => { setShowPrivacyModal(false); setPrivacyOpened(true); }} />
                  </div>
                  <div style={{ padding: '20px', overflowY: 'auto', flex: 1, fontSize: '0.9rem', lineHeight: '1.6', color: '#333' }}>
                      <h2 style={{ color: '#b30000', fontSize: isMobile ? '1.5rem' : '2rem', borderBottom: '2px solid #b30000', paddingBottom: '10px', marginTop: 0 }}>
                          Adatkezelési Tájékoztató
                      </h2>

                      <h3><b>Adatkezelési Tájékoztató – ArcheryNexus Szolgáltatások</b></h3>

                      <p><b>Hatálybalépés dátuma:</b> 2026. február 27.</p>

                      <p>Jelen Adatkezelési Tájékoztató (a továbbiakban: <b>Tájékoztató</b>) célja, hogy tájékoztassa Önt, mint az ArcheryNexus mobilalkalmazás (a továbbiakban: <b>Alkalmazás</b>) és a hozzá kapcsolódó weboldal (a továbbiakban: <b>Weboldal</b>) (az Alkalmazás és a Weboldal a továbbiakban együttesen: <b>Szolgáltatások</b>) felhasználóját (a továbbiakban: <b>Felhasználó</b> vagy <b>Ön</b>) az Ön személyes adatainak kezelésével kapcsolatos gyakorlatunkról, az Általános Adatvédelmi Rendelet (GDPR, az Európai Parlament és Tanács 2016/679. rendelete) és a vonatkozó magyar adatvédelmi jogszabályok (különösen az információs önrendelkezési jogról és az információszabadságról szóló 2011. évi CXII. törvény – Infotv.) előírásainak megfelelően.</p>

                      <h4><b>1. Az Adatkezelő személye és elérhetőségei</b></h4>
                      <p>A személyes adatok kezelője (a továbbiakban: <b>Adatkezelő</b>):<br/>
                      <b>Név:</b> Márki Róbert Viktor<br/>
                      <b>E-mail cím:</b> robert.marki@live.com</p>
                      <p>Az Adatkezelő elkötelezett a Szolgáltatásokat használó Felhasználók személyes adatainak és magánéletének védelme iránt. Jelen Tájékoztató ismerteti, hogy az Adatkezelő hogyan gyűjti, használja fel és bizonyos esetekben hogyan oszthatja meg az Ön személyes adatait.</p>

                      <h4><b>2. A kezelt személyes adatok köre, az adatkezelés célja és jogalapja</b></h4>
                      <p>A Szolgáltatások használata során az Adatkezelő az alábbi személyes adatokat kezelheti:</p>

                      <p><b>Regisztrációs adatok (Alkalmazás és Weboldal esetén is):</b></p>
                      <ul>
                          <li><b>Kezelt adatok:</b> Vezetéknév, keresztnév, e-mail cím, jelszó (titkosított formában).</li>
                          <li><b>Az adatkezelés célja:</b> A Felhasználó azonosítása, felhasználói fiók létrehozása és kezelése, kapcsolattartás, a Szolgáltatások alapvető funkcióinak biztosítása.</li>
                          <li><b>Az adatkezelés jogalapja:</b> Az Ön hozzájárulása (GDPR 6. cikk (1) bekezdés a) pont), amelyet a regisztráció során ad meg, valamint a Szolgáltatások használatára vonatkozó szerződés teljesítése (GDPR 6. cikk (1) bekezdés b) pont).</li>
                      </ul>

                      <p><b>Közösségi adatok és korhatár-ellenőrzés:</b></p>
                      <ul>
                          <li><b>Kezelt adatok:</b> Születési dátum (év, hónap, nap), profilkép (opcionális), ismerősök listája (UID azonosítók), megosztott fényképek.</li>
                          <li><b>Az adatkezelés célja:</b> A 16 éves korhatár ellenőrzése a jogszabályi megfelelőség (GDPR) érdekében, a QR-kód alapú ismerősi rendszer működtetése, valamint a vizuális tartalommegosztás biztosítása a visszaigazolt ismerősök között.</li>
                          <li><b>Az adatkezelés jogalapja:</b> Az Ön kifejezett hozzájárulása (GDPR 6. cikk (1) bekezdés a) pont), amelyet a születési dátum megadásakor és a közösségi funkciók aktiválásakor ad meg.</li>
                      </ul>

                      <p><b>Felhasználói profil, edzés- és versenyadatok:</b></p>
                      <ul>
                          <li><b>Kezelt adatok:</b>
                              <ul>
                                  <li>A regisztráció során megadott vezetéknév, keresztnév és e-mail cím.</li>
                                  <li>A Magyar Íjász Szövetség által kiadott versenyengedély száma (amennyiben megadja).</li>
                                  <li>Egyesületi tagságra vonatkozó adatok.</li>
                                  <li>Edzések során rögzített adatok: lőeredmények, dátumok, helyszínek, jegyzetek.</li>
                                  <li>Felszerelés típusa és beállításai.</li>
                                  <li>Automatikusan képzett statisztikák (átlagpontszám, találati arányok).</li>
                                  <li>Versenyeken elért eredmények.</li>
                              </ul>
                          </li>
                          <li><b>Az adatkezelés célja:</b> A Szolgáltatások fő funkcióinak biztosítása.</li>
                          <li><b>Az adatkezelés jogalapja:</b> Az Ön hozzájárulása és a szerződés teljesítése.</li>
                      </ul>

                      <h4><b>3. Az adatkezelés időtartama</b></h4>
                      <ul>
                          <li>Regisztrációs és profiladatok: a hozzájárulás visszavonásáig (fiók törléséig), vagy 2 év inaktivitásig.</li>
                          <li>Edzés- és versenyadatok: a fiók törléséig.</li>
                          <li>Eszköz- és használati adatok: legfeljebb 1 évig.</li>
                      </ul>

                      <h4><b>4. Adatfeldolgozók és adattovábbítás</b></h4>
                      <p><b>Firebase (Google Ireland Limited):</b> Hitelesítésre, adattárolásra és fényképek tárolására.</p>
                      <p><b>Közösségi Funkciók:</b> Az adatok kizárólag a visszaigazolt ismerősök (QR-kód alapú jelölés) számára láthatóak.</p>

                      <h4><b>5. Adatbiztonság</b></h4>
                      <p>Megfelelő technikai és szervezési intézkedéseket teszünk a személyes adatok biztonsága érdekében.</p>

                      <h4><b>6. Az Ön jogai (Érintetti jogok)</b></h4>
                      <p>Ön rendelkezik a tájékoztatáshoz, hozzáféréshez, helyesbítéshez, törléshez, korlátozáshoz, adathordozhatósághoz és tiltakozáshoz való joggal.</p>

                      <h4><b>7. Jogorvoslati lehetőségek</b></h4>
                      <p>Panaszt tehet a Nemzeti Adatvédelmi és Információszabadság Hatóságnál (NAIH), vagy bírósághoz fordulhat.</p>

                      <h4><b>8. Kiskorúak védelme és a 16 éves korhatár</b></h4>
                      <p>A közösségi funkciók elérését 16 éves életkorhoz kötjük. 16 év alattiak számára ezen funkciók inaktívak.</p>

                      <h4><b>9. A Tájékoztató módosítása</b></h4>
                      <p>Az Adatkezelő fenntartja a jogot a Tájékoztató egyoldalú módosítására.</p>

                      <h4><b>10. Alapvető fogalmak</b></h4>
                      <p>Részletes fogalommeghatározások a GDPR szerint (Személyes adat, Érintett, Adatkezelő, stb.).</p>

                      <hr/>
                      <p style={{ marginTop: '2rem', fontSize: '0.8rem', fontStyle: 'italic', color: '#666' }}>
                          Nyomtatva: {new Date().toLocaleDateString('hu-HU')}
                      </p>
                  </div>
                  <div style={{ padding: '15px', borderTop: '1px solid #eee', textAlign: 'center' }}>
                      <button
                        style={{ backgroundColor: '#b30000', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                        onClick={() => { setShowPrivacyModal(false); setPrivacyOpened(true); }}
                      >
                          Megértettem és bezárom
                      </button>
                  </div>
              </div>
          </div>
      )}
    </main>
  );
};

export default RegisterForm;
